/**
 * Ознака входу й завантаження метаданих з нею — окремо від `meta.ts`, без TanStack Query: це
 * бере і документ входу (login.html), а його JS тримаємо в бюджеті ≤ 100 КБ (група size-limit
 * «Вхід»). Застосунок імпортує те саме через `meta.ts`.
 */
import type { AppMeta } from '@clientline/contracts';
import { ApiProblem, fetchMeta } from './api.ts';

/**
 * Ознака «людина входила в цій вкладці / браузері» (не секрет і не доказ входу — cookie сесії
 * HttpOnly, її скрипт не бачить). Потрібна оболонці, щоб без входу не ходити в API на першому
 * екрані: інакше кожен холодний старт без сесії — зайві запит і 401 у консолі (бюджет
 * performance-budget.md, розд. 3). Справжню перевірку робить сервер на кожному запиті.
 * Ключ має збігатися з public/boot.js: він за цією ознакою вибирає ранні запити.
 */
const SESSION_HINT = 'clientline.signedIn';

export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT) === '1';
  } catch {
    return false;
  }
}

const hintListeners = new Set<() => void>();

function setSessionHint(signedIn: boolean): void {
  const before = hasSessionHint();
  try {
    if (signedIn) localStorage.setItem(SESSION_HINT, '1');
    else localStorage.removeItem(SESSION_HINT);
  } catch {
    // Сховище недоступне (приватний режим) — оболонка просто не покаже пунктів модулів до входу.
  }
  if (hasSessionHint() !== before) for (const listener of hintListeners) listener();
}

/**
 * Підписка на зміну ознаки входу: у цій вкладці й в інших (подія `storage` приходить лише в
 * інші вкладки того самого походження). Вхід в іншій вкладці вмикає оболонці запит `/meta`
 * без перезавантаження (рев'ю Frontend #72 → E3a-06b). `null` у `key` — `localStorage.clear()`.
 */
export function subscribeSessionHint(listener: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === SESSION_HINT || event.key === null) listener();
  };
  hintListeners.add(listener);
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    hintListeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** Вихід: оболонка й boot.js більше не вважають вкладку «з входом». */
export function clearSessionHint(): void {
  setSessionHint(false);
}

/** Метадані з позначкою входу: успіх — людина ввійшла, 401 — сесії немає. */
export async function loadMeta(signal?: AbortSignal): Promise<AppMeta> {
  try {
    const meta = await fetchMeta(signal);
    setSessionHint(true);
    return meta;
  } catch (error) {
    if (error instanceof ApiProblem && error.status === 401) setSessionHint(false);
    throw error;
  }
}
